import type { RouteRecordRaw } from 'vue-router'

import { creativeAiLandingIngressRoutes, legacyIngressRoutes } from '../ingress'
import { HOME_ROUTE_NAME } from '../routeNames'

import { callbackRoutes } from './callbacks'
import { legacySettingsRoutes } from './legacySettings'
import { mcpEnableRoutes, mcpShellRoutes } from './mcp'

import { alertRoutes } from '@/features/alerts'
import { anomalyRoutes } from '@/features/anomalies'
import { audienceRoutes } from '@/features/audience'
import { authRoutes } from '@/features/auth'
import { commerceRoutes } from '@/features/commerce-analytics'
import { creativeAiRoutes } from '@/features/creative-ai'
import { creditRoutes } from '@/features/credits'
import { dashboardRoutes } from '@/features/dashboards'
import { dataConnectorRoutes } from '@/features/data-connectors'
import { dataExportRoutes } from '@/features/data-export'
import { getStartedRoutes, onboardingRoutes } from '@/features/global-surface'
import { healthCheckRoutes } from '@/features/health-checks'
import { impactRoutes } from '@/features/impact-analysis'
import { insightRoutes } from '@/features/insights'
import { mcpRoutes, sheetsAddonRoutes, slackLinkRoutes } from '@/features/integrations'
import { marketingInsightsRoutes } from '@/features/marketing-insights'
import { orpheusRoutes } from '@/features/orpheus'
import { platformActionRoutes } from '@/features/platform-actions'
import { reportRoutes } from '@/features/reports'
import { scorecardRoutes } from '@/features/scorecards'
import { summaryRoutes } from '@/features/summary'
import { workflowRoutes } from '@/features/workflow'
import { workspaceSettingsRoutes } from '@/features/workspace-settings'

// Composes every features/<f>/routes.ts under the layout parents (contract 05 §11) — the
// router never reaches into feature internals. Named `appRoutes` per contract §4 (the ticket
// ratifies this over module 01's interim `routes`). Auth (06) contributes the entry funnel via
// its barrel (meta.layout:'auth', requiresAuth:false, guestOnly for pre-auth screens);
// features 07–19 append their route modules to the '/' parent's children as they land.
export const appRoutes: RouteRecordRaw[] = [
  ...authRoutes,
  // The blank-layout provider-return surface (contract §11) — declared before the '/' parent so
  // a callback path can never be swallowed by an app-surface route.
  ...callbackRoutes,
  // 12's Slack per-user link page (ORP-19904). Declared beside the callback surface rather than
  // under the '/' parent: it carries an absolute `/settings/slack-link` path that must match the
  // backend's invite constant exactly, and it renders standalone rather than inside the settings
  // shell. `/settings` has no `slack-link` child, so nothing here shadows the settings host.
  ...slackLinkRoutes,
  // 12's Google Sheets add-on approval page (ORP-27820) — same standalone shape: the add-on's build
  // fixes `/sheets-addon/connect#pairing=<uuid>`, and the page renders outside the app shell.
  ...sheetsAddonRoutes,
  // 29's blank-layout onboarding wizard (contract 29 §11). Declared beside the other blank-layout
  // surfaces rather than under the '/' parent: it is the *pre-workspace* journey, so it must not
  // inherit the app shell that assumes a selected workspace, and `permissionGuard` sends a
  // zero-workspace user here instead of leaving the empty-workspace CTA a dead click.
  ...onboardingRoutes,
  // The MCP-only product surface (ORP-21466). Declared BESIDE the '/' parent, not under it: it
  // owns a different shell (`layout: 'mcp'`) and shows none of the web product's chrome, so it
  // must not inherit AppLayout. Its own parent carries `requiresAuth`.
  ...mcpShellRoutes,
  // …and the web-layout card a NON-MCP account gets when it opens one of those screens
  // (ORP-21836). Beside the shell rather than under it, for the reasons on the record itself.
  ...mcpEnableRoutes,
  {
    // The authenticated app surface: children inherit `requiresAuth` + `layout` through
    // vue-router's matched-meta merge. App.vue selects AppLayout; that layout owns the boot gate.
    path: '/',
    redirect: { name: HOME_ROUTE_NAME },
    meta: { layout: 'app', requiresAuth: true },
    children: [
      ...summaryRoutes,
      ...getStartedRoutes,
      ...orpheusRoutes,
      ...anomalyRoutes,
      ...alertRoutes,
      ...healthCheckRoutes,
      ...platformActionRoutes,
      ...impactRoutes,
      ...marketingInsightsRoutes,
      ...reportRoutes,
      ...dashboardRoutes,
      ...commerceRoutes,
      ...dataConnectorRoutes,
      ...dataExportRoutes,
      ...creditRoutes,
      // ORP-20039 landed the landing target; the detail route (path/name a 1:1 match with v3's
      // `creative-ai/:analysisId/:tab?`) now registers alongside it so the landing page's own
      // `router.push({ name: 'CreativeAiDetail' })` resolves. Its *legacy* ingress path stays an
      // explicit pending redirect until ORP-18906 owns that separate cutover — unrelated to this.
      ...creativeAiRoutes,
      ...scorecardRoutes,
      ...audienceRoutes,
      // 15-D's workbench is intentionally standalone until the module-22 placement installer
      // lands. Put the specific child before the broader settings parent so the production route
      // cannot be swallowed while keeping the leaf independent of its eventual host.
      ...insightRoutes,
      ...mcpRoutes,
      ...workspaceSettingsRoutes,
      ...workflowRoutes,
    ],
  },
  // v3's `/workspace-settings/*` URLs → their v4 pane. MUST precede the catch-all, which would
  // otherwise swallow every legacy settings link and drop it on Summary (ORP-19905).
  ...legacySettingsRoutes,
  // Every audited, known-but-not-yet-v4 ingress gets a truthful, typed outcome before the
  // genuinely-unknown catch-all. Generated from one registry so tests and runtime cannot drift.
  ...creativeAiLandingIngressRoutes,
  ...legacyIngressRoutes,
  {
    // Unknown route → home (legacy parity: catchAll → '/' → '/summary';
    // v3 index.js:27–30 + routes/app.js:5–7).
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: { name: HOME_ROUTE_NAME },
  },
]
